import { createClient } from "@supabase/supabase-js";

export interface AuthClient {
  getAccessToken(): Promise<string | null>;
  signInWithPassword?: (email: string, password: string) => Promise<void>;
}

export interface BrowserAuthEnvironment {
  readonly [key: string]: string | undefined;
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
}

export class AuthConfigurationError extends Error {
  readonly code = "auth_configuration";

  constructor(
    message = "Supabase authentication is not configured for this browser.",
  ) {
    super(message);
    this.name = "AuthConfigurationError";
  }
}

export class AuthProviderError extends Error {
  readonly code = "auth_provider_error";

  constructor(message: string) {
    super(message);
    this.name = "AuthProviderError";
  }
}

interface SupabaseSessionClient {
  auth: {
    getSession(): Promise<{
      data: { session: { access_token: string } | null };
      error: { message: string } | null;
    }>;
    signInWithPassword?: (credentials: {
      email: string;
      password: string;
    }) => Promise<{
      data: { session: { access_token: string } | null };
      error: { message: string } | null;
    }>;
  };
}

/**
 * The API boundary remains independent of provider details. This adapter reads
 * and creates the approved Supabase browser session and exposes only the
 * access-token and sign-in operations to React and the API client. CRM
 * identity, role, and capabilities still come from the server's /me response.
 */
export function createSupabaseAuthClient(
  supabase: SupabaseSessionClient,
): AuthClient {
  return {
    async getAccessToken(): Promise<string | null> {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw new AuthProviderError(error.message);
      return data.session?.access_token ?? null;
    },
    async signInWithPassword(email: string, password: string): Promise<void> {
      if (!supabase.auth.signInWithPassword) {
        throw new AuthProviderError(
          "Email/password sign-in is not available from the configured identity provider.",
        );
      }
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw new AuthProviderError(error.message);
    },
  };
}

export function createEnvironmentAuthClient(
  environment: BrowserAuthEnvironment = import.meta.env,
): AuthClient {
  const supabaseUrl = environment.VITE_SUPABASE_URL?.trim() ?? "";
  const supabaseAnonKey = environment.VITE_SUPABASE_ANON_KEY?.trim() ?? "";

  if (!supabaseUrl || !supabaseAnonKey) {
    return {
      async getAccessToken(): Promise<string | null> {
        throw new AuthConfigurationError(
          "Supabase authentication is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY for the web app.",
        );
      },
    };
  }

  const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: true,
    },
  });
  return createSupabaseAuthClient(supabase);
}
